import { Button } from '@design/components/button/Button';
import { useFollowedTopics } from '@terminal/react-query-hooks/topics/useFollowedTopics';
import { useFollowTopic } from '@terminal/react-query-hooks/topics/useFollowTopic';
import { useUnfollowTopic } from '@terminal/react-query-hooks/topics/useUnfollowTopic';
import clsx from 'clsx';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './FollowThemeButton.module.scss';

export interface FollowThemeButtonProps {
  themeId: string;
  className?: string;
}

export const FollowThemeButton = ({
  themeId,
  className,
}: FollowThemeButtonProps) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const { data } = useFollowedTopics('theme');
  const { mutate: followTopic, isPending: isFollowingPending } =
    useFollowTopic();
  const { mutate: unfollowTopic, isPending: isUnfollowingPending } =
    useUnfollowTopic();

  const isFollowed = data?.values.includes(themeId) ?? false;
  const isPending = isFollowingPending || isUnfollowingPending;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isFollowed) {
      unfollowTopic({ type: 'theme', code: themeId });
    } else {
      followTopic({ type: 'theme', code: themeId });
    }
  };

  return (
    <Button
      className={clsx(
        styles.followThemeButton,
        { [styles.active]: isFollowed },
        className
      )}
      data-testid="follow-theme-button"
      label={
        isFollowed
          ? t('common:components.followThemeBtn.followingLabel')
          : t('common:components.followThemeBtn.followThemeLabel')
      }
      size="small"
      variant="tertiary"
      iconName={isFollowed ? 'Checkmark' : 'Add'}
      disabled={isPending}
      onClick={handleClick}
    />
  );
};
